import { Injectable } from '@angular/core';
import { UIEntityItem } from '../entity-list/ui-entity-item/ui-entity-item';
import { Subject, Observable } from 'rxjs';
import { TabData } from './entity-details-tab/tab-data';
import { TAB_ACTION } from './entity-details-tab/tab-action';
import { DIALOG_VISIBILITY } from '../core/const/dialog-visibility';
import { GridResponse } from '../core/grid/grid-response';

import { AlertErrorDataService } from '../alert/alert-error/alert-error-data.service';
import { ALERT_ERROR_NAME } from '../alert/alert-error/alert-error-name';
import { AlertFlashDataService } from '../alert/alert-flash/alert-flash-data.service';
import { UI_ENTITY_ITEM_ACTION } from '../entity-list/const/ui-entity-item-action';
import { PendingChange } from './pending-changes/pending-change';
import { RoleItem } from '../entity-list/base-entity-item/role-item';
import { ServiceItem } from '../entity-list/base-entity-item/service-item';

@Injectable({
  providedIn: 'root'
})
export class EntityDetailsDataService {

  private _pendingChanges: GridResponse<PendingChange> = { content: [], totalNumberOfRecords: 0 };
  private _resetServiceAccess = false;
  private _currentEntityType: string = '';

  private _readOnly = false;
  private _routerUrl = '';

  private _uiEntityItem = new Subject<UIEntityItem>();
  public uiEntityItem$: Observable<UIEntityItem> = this._uiEntityItem.asObservable();

  private _detailsDialog = new Subject<string>();
  public detailsDialog$: Observable<string> = this._detailsDialog.asObservable();

  private _discardChangesDialog = new Subject<string>();
  public discardChangesDialog$: Observable<string> = this._discardChangesDialog.asObservable();

  private _confirmationDialog = new Subject<string>();
  public confirmationDialog$: Observable<string> = this._confirmationDialog.asObservable();

  private _refreshServiceAccess = new Subject<boolean>();
  public refreshServiceAccess$: Observable<boolean> = this._refreshServiceAccess.asObservable();

  private _tab = new Subject<TabData>();
  public tab$: Observable<TabData> = this._tab.asObservable();

  private _pushPendingChange = new Subject<PendingChange>();
  public pushPendingChange$: Observable<PendingChange> = this._pushPendingChange.asObservable();

  private _popPendingChange = new Subject<string>();
  public popPendingChange$: Observable<string> = this._popPendingChange.asObservable();

  private _clearPendingChanges = new Subject<boolean>();
  public clearPendingChanges$: Observable<boolean> = this._clearPendingChanges.asObservable();

  private _enableAction = new Subject<PendingChange[]>();
  public enableAction$: Observable<PendingChange[]> = this._enableAction.asObservable();

  private _refreshMembersGrid = new Subject<boolean>();
  public refreshMembersGrid$: Observable<boolean> = this._refreshMembersGrid.asObservable();

  private _includeAllMembers = new Subject<boolean>();
  public includeAllMembers$: Observable<boolean> = this._includeAllMembers.asObservable();

  private _makeItReadOnly = new Subject<boolean>();
  public makeItReadOnly$: Observable<boolean> = this._makeItReadOnly.asObservable();

  private _removeReadOnly = new Subject<boolean>();
  public removeReadOnly$: Observable<boolean> = this._removeReadOnly.asObservable();

  private _password = new Subject<string>();
  public password$: Observable<string> = this._password.asObservable();

  constructor(private alertFlashDataService: AlertFlashDataService, private alertErrorDataService: AlertErrorDataService) { }

  getIncludeAllMembersSessionStorageKey(type: string, id: string, memberType?: string): string {
    if (memberType && memberType.length) {
      return `${type}_${id}_${memberType}`;
    }
    return `${type}_${id}`;
  }

  sendPassword(password: string): void {
    this._password.next(password);
  }

  includeAllMembers(): void {
    this._includeAllMembers.next(true);
  }

  isReadOnly(): boolean {
    return this._readOnly;
  }

  setReadOnly(value: boolean) {
    this._readOnly = value;
  }

  makeItReadOnly(): void {
    this._makeItReadOnly.next(true);
  }

  removeReadOnly(): void {
    this._removeReadOnly.next(true);
  }

  get routerUrl(): string {
    return this._routerUrl;
  }

  set routerUrl(value: string) {
    this._routerUrl = value;
  }

  isResetServiceAccess(): boolean {
    return this._resetServiceAccess;
  }

  setResetServiceAccess(value: boolean) {
    this._resetServiceAccess = value;
  }

  get currentEntityType(): string {
    return this._currentEntityType;
  }

  set currentEntityType(type: string) {
    this._currentEntityType = type;
  }

  createOrUpdateUIEntityItem(uiEntityItem: UIEntityItem): void {
    this._uiEntityItem.next(uiEntityItem);
  }

  deleteUIEntityItem(uiEntityItem: UIEntityItem): void {
    uiEntityItem.action = UI_ENTITY_ITEM_ACTION.delete;
    this._uiEntityItem.next(uiEntityItem);
  }

  changeCompanyStatus(uiEntityItem: UIEntityItem): void {
    uiEntityItem.action = UI_ENTITY_ITEM_ACTION.changeStatus;
    this._uiEntityItem.next(uiEntityItem);
  }

  preventTabChange(): void {
    this._tab.next({ action: TAB_ACTION.preventChange, tabIndex: -1 });
  }

  allowTabChange(): void {
    this._tab.next({ action: TAB_ACTION.allowChange, tabIndex: -1 });
  }

  showTab(tabIndex: number): void {
    this._tab.next({ action: TAB_ACTION.show, tabIndex: tabIndex });
  }

  showDetailsDialog(): void {
    this._detailsDialog.next(DIALOG_VISIBILITY.show);
  }

  hideDetailsDialog(): void {
    this.alertErrorDataService.alertErrorName = ALERT_ERROR_NAME.main;
    this._currentEntityType = '';
    this.alertFlashDataService.reset();
    this.alertErrorDataService.reset();
    this._tab.next({ action: TAB_ACTION.resetActive, tabIndex: -1 });
    this._detailsDialog.next(DIALOG_VISIBILITY.hide);
  }

  showDiscardChangesDialog(): void {
    this._discardChangesDialog.next(DIALOG_VISIBILITY.show);
  }

  hideDiscardChangesDialog(): void {
    this._discardChangesDialog.next(DIALOG_VISIBILITY.hide);
  }

  showConfirmationDialog(): void {
    this._confirmationDialog.next(DIALOG_VISIBILITY.show);
  }

  hideConfirmationDialog(): void {
    this._confirmationDialog.next(DIALOG_VISIBILITY.hide);
  }

  hideConfirmationDialogAndRefresh(): void {
    this._confirmationDialog.next(DIALOG_VISIBILITY.hideAndRefresh);
  }

  refreshServiceAccess(): void {
    this._refreshServiceAccess.next(true);
  }

  refreshMembersGrid(): void {
    this._refreshMembersGrid.next(true);
  }

  hasPendingChanges(): boolean {
    let has = false;
    if (this._pendingChanges.totalNumberOfRecords > 0) {
      has = true;
    }
    return has;
  }

  getPendingChanges(): PendingChange[] {
    return this._pendingChanges.content;
  }

  getPendingChangesByAction(action: string): PendingChange[] {
    return this._pendingChanges.content.filter(obj => obj.action === action);
  }

  getPendingChangesNumber(): number {
    return this._pendingChanges.totalNumberOfRecords;
  }

  pushPendingChange(entityId: string, name: string, action: string, actionText: string, validTo?: number): void {
    this.push(entityId, name, action, actionText, validTo);
  }

  popPendingChange(entityId: string): void {
    const content = this._pendingChanges.content.filter(obj => obj.entityId === entityId);
    this._pendingChanges.content = this._pendingChanges.content.filter(obj => obj.entityId !== entityId);
    this._pendingChanges.totalNumberOfRecords -= 1;
    this._enableAction.next(content);
    this._popPendingChange.next(entityId);
  }

  clearPendingChanges(): void {
    this._pendingChanges.content = [];
    this._pendingChanges.totalNumberOfRecords = 0;
    this._clearPendingChanges.next(true);
  }

  clearPendingChangesAndEnableAction(): void {
    const content = this._pendingChanges.content;
    this._pendingChanges.content = [];
    this._pendingChanges.totalNumberOfRecords = 0;
    this._enableAction.next(content);
  }

  updateItemStatus(content: any[], changes: PendingChange[], idKey: string, action: string, status: boolean): any[] {
    for (let i = 0, len = content.length; i < len; i++) {
      for (let j = 0, jlen = changes.length; j < jlen; j++) {
        if (content[i][idKey] === changes[j].entityId && changes[j].action === action) {
          content[i].hasActionDisabled = status;
        }
      }
    }
    return content;
  }

  updateRolesStatus(rows: ServiceItem[], changes: PendingChange[], action: string, status: boolean): any[] {
    for (let i = 0, len = rows.length; i < len; i++) {
      const roles = rows[i].roles;
      if (roles) {
        this.updateHasActionDisabled(roles, changes, action, status)
      }
    }
    return rows;
  }

  updateRoleStatus(rows: ServiceItem[], roleInvariantKey: string, value: boolean): ServiceItem[] {
    for (let i = 0, ilen = rows.length; i < ilen; i++) {
      const roles = rows[i].roles;
      if (roles) {
        for (let j = 0, jlen = roles.length; j < jlen; j++) {
          if (roles[j].roleInvariantKey === roleInvariantKey) {
            roles[j].hasActionDisabled = value
            break;
          }
        }
      }
    }
    return rows;
  }

  enableItems(arr: any[], changes: PendingChange[], idField: string): any[] {
    for (let i = 0, len = changes.length; i < len; i++) {
      for (let j = 0, jlen = arr.length; j < jlen; j++) {
        if (arr[j][idField] === changes[i].entityId) {
          arr[j].hasActionDisabled = false;
        }
      }
    }
    return arr;
  }

  copyInputMessage(event: Event) {
    const span = event.currentTarget as HTMLSpanElement;
    const input = span.previousElementSibling as HTMLInputElement;
    navigator.clipboard.writeText(input.value);
  }

  private push(entityId: any, name: string, action: string, actionText: string, validTo?: number): void {
    let entity: PendingChange = { entityId: entityId, name: name, action: action, actionText: actionText };
    if (validTo) {
      entity = { entityId: entityId, name: name, action: action, actionText: actionText, validTo: validTo };
    }

    const index = this.indexOf(entityId);
    if (index > -1) {
      this.pop(entityId);
    }
    this._pendingChanges.content.push(entity);
    this._pendingChanges.totalNumberOfRecords += 1;
    this._pushPendingChange.next(entity);
  }

  private pop(entityId: any): void {
    this._pendingChanges.content = this._pendingChanges.content.filter(obj => obj.entityId !== entityId);
    this._pendingChanges.totalNumberOfRecords -= 1;
    this._popPendingChange.next(entityId);
  }

  private indexOf(entityId: any): number {
    let index = -1;
    for (let i = 0, len = this._pendingChanges.content.length; i < len; i++) {
      if (this._pendingChanges.content[i].entityId === entityId) {
        index = i;
        break;
      }
    }
    return index;
  }

  private updateHasActionDisabled(roles: RoleItem[], changes: PendingChange[], action: string, status: boolean) {
    for (let j = 0, jlen = roles.length; j < jlen; j++) {
      for (let k = 0, klen = changes.length; k < klen; k++) {
        if (roles[j].roleInvariantKey === changes[k].entityId && changes[k].action === action) {
          roles[j].hasActionDisabled = status;
        }
      }
    }
  }

}
